/**
 * Rolled Steel Unequal Angles - Data Structure
 * Based on "ROLLED STEEL UNEQUAL ANGLES dimensions and properties.csv"
 * 
 * UNEQUAL ANGLES (A ≠ B):
 * - Two perpendicular legs of DIFFERENT lengths (major leg A, minor leg B)
 * - 10 Essential Variables: A, B, t, r1, r2, Cxx, Cyy, tanα (principal axis angle)
 * - Principal axes U-U and V-V are NOT at 45° (unlike equal angles)
 * - tanα defines the rotation angle of principal axis U-U from X-X axis
 */

export interface RolledSteelUnequalAngleData {
  designation: string;   // e.g., "ISA 3020"
  size: string;          // e.g., "30 x 20" (A × B in mm)
  
  // 10 Essential Variables
  A: number;             // Major leg length (mm)
  B: number;             // Minor leg length (mm)
  t: number;             // Thickness (mm)
  r1: number;            // Root radius - internal fillet (mm)
  r2: number;            // Toe radius - external edge curve (mm)
  Cxx: number;           // Centroid from back of major leg (cm)
  Cyy: number;           // Centroid from back of minor leg (cm)
  tanAlpha: number;      // tan(α) - principal axis rotation angle
  
  // Additional geometric properties
  exx: number;           // Distance from centroid to edge X (cm)
  eyy: number;           // Distance from centroid to edge Y (cm)
  
  // Physical properties
  w: number;             // Weight per meter (kg/m)
  a: number;             // Cross-sectional area (cm²)
  
  // Structural properties - X-X and Y-Y axes (centroidal)
  Ixx: number;           // Moment of inertia about X-X (cm⁴)
  Iyy: number;           // Moment of inertia about Y-Y (cm⁴)
  rxx: number;           // Radius of gyration about X-X (cm)
  ryy: number;           // Radius of gyration about Y-Y (cm)
  Zxx: number;           // Section modulus about X-X (cm³)
  Zyy: number;           // Section modulus about Y-Y (cm³)
  
  // Structural properties - U-U and V-V axes (principal)
  Iuu: number;           // Moment of inertia about U-U (cm⁴) - maximum
  Ivv: number;           // Moment of inertia about V-V (cm⁴) - minimum
  ruu: number;           // Radius of gyration about U-U (cm)
  rvv: number;           // Radius of gyration about V-V (cm)
  Ixy: number;           // Product of inertia (cm⁴)
}

/**
 * Complete dataset of Rolled Steel Unequal Angle sections from CSV
 * All dimensions and properties from standard rolled steel angle specifications
 */
export const rolledSteelUnequalAngles: RolledSteelUnequalAngleData[] = [
  {
    designation: 'ISA 3020',
    size: '30 x 20',
    A: 30,
    B: 20,
    t: 3,
    r1: 4.5,
    r2: 3,
    Cxx: 0.98,
    Cyy: 0.49,
    exx: 2.02,
    eyy: 1.51,
    tanAlpha: 0.43,
    w: 1.1,
    a: 1.41,
    Ixx: 1.2,
    Iyy: 0.4,
    Iuu: 1.4,
    Ivv: 0.2,
    Ixy: 0.4,
    rxx: 0.92,
    ryy: 0.54,
    ruu: 0.99,
    rvv: 0.41,
    Zxx: 0.6,
    Zyy: 0.3,
  },
  {
    designation: 'ISA 4025',
    size: '40 x 25',
    A: 40,
    B: 25,
    t: 3,
    r1: 5,
    r2: 3,
    Cxx: 1.3,
    Cyy: 0.57,
    exx: 2.7,
    eyy: 1.93,
    tanAlpha: 0.38,
    w: 1.5,
    a: 1.88,
    Ixx: 3.0,
    Iyy: 0.9,
    Iuu: 3.3,
    Ivv: 0.5,
    Ixy: 0.9,
    rxx: 1.25,
    ryy: 0.68,
    ruu: 1.33,
    rvv: 0.52,
    Zxx: 1.1,
    Zyy: 0.5,
  },
];

/**
 * Get all available unequal angle designations
 */
export const getRolledSteelUnequalAngleDesignations = (): string[] => {
  return rolledSteelUnequalAngles.map((angle) => angle.designation);
};

/**
 * Get unequal angle data by designation
 */
export const getRolledSteelUnequalAngleByDesignation = (
  designation: string
): RolledSteelUnequalAngleData | undefined => {
  return rolledSteelUnequalAngles.find((angle) => angle.designation === designation);
};
